import React, { useState, useRef, useEffect } from "react";
import { BsX } from "react-icons/bs";
import { IoDocumentAttachOutline } from "react-icons/io5";
import "./GlobalStyle.css";
import {
  addpostbasedonhrms,
  getAllPostsWithPagination,
  getEmployeeDetails,
} from "../../api/ApiConfig";
import { toast } from "react-hot-toast";
import Loading from "../loading/Loading";

const ModalComponent = ({ show, handleClose }) => {
  const [posts_contents, set_posts_contents] = useState("");
  const [posts_type, set_posts_type] = useState("");
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [hasInvalidFile, setHasInvalidFile] = useState(false); // NEW STATE: Tracks if any file is too large
  
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const [employeeDetails, setEmployeeDetails] = useState({});

  // Constants for file size limits in bytes
  const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
  const MAX_VIDEO_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

  // Close modal when clicking outside
  useEffect(() => {
    const listener = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };
    if (show) document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [show, handleClose]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getEmployeeDetails();
      if (data) {
        setEmployeeDetails(data);
      } else {
        setEmployeeDetails({});
      }
    };

    fetchData();

    if (!show) {
      // Reset all states when modal closes
      set_posts_contents("");
      set_posts_type("");
      // Correctly revoke Object URLs for cleanup
      files.forEach((file) => URL.revokeObjectURL(URL.createObjectURL(file)));
      setFiles([]);
      setIsLoading(false); 
      setHasInvalidFile(false); // NEW: Reset invalid file status
    }

    // Cleanup function: Revoke URLs when component unmounts or files change
    return () => {
      files.forEach((file) => URL.revokeObjectURL(URL.createObjectURL(file)));
    };
  }, [show]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];
    const invalidFiles = [];
    let foundInvalid = false; // Local flag

    selectedFiles.forEach((file) => {
      // Check file type and size
      if (file.type.startsWith("image/") || file.type.startsWith("image/gif")) {
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
          invalidFiles.push({ name: file.name, type: "image" });
          foundInvalid = true;
        } else {
          validFiles.push(file);
        }
      } else if (file.type.startsWith("video/")) {
        if (file.size > MAX_VIDEO_SIZE_BYTES) {
          invalidFiles.push({ name: file.name, type: "video" });
          foundInvalid = true;
        } else {
          validFiles.push(file);
        }
      } else {
        // Allow other files by default (documents)
        validFiles.push(file);
      }
    });

    // Update state with valid files
    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
    }

    // NEW: Update the global invalid file status
    if (foundInvalid || invalidFiles.length > 0) {
      setHasInvalidFile(true);
    } else {
      // If we only added valid files, we must re-check ALL current files
      // to see if the removal of a previously invalid file should reset the state.
      // However, since we only *add* files here, we only set it to true if new invalid files are found.
      // The state must be cleared when removing files (see handleRemoveAttachment).
    }


    // Show toast for invalid files
    if (invalidFiles.length > 0) {
      const imageErrors = invalidFiles
        .filter((f) => f.type === "image")
        .map((f) => f.name);
      const videoErrors = invalidFiles
        .filter((f) => f.type === "video")
        .map((f) => f.name);

      if (imageErrors.length > 0) {
        toast.error(
          `Image(s) too large. Max size is 2MB.`,
          { duration: 6000 }
        );
      }
      if (videoErrors.length > 0) {
        toast.error(
          `Video(s) too large. Max size is 10MB.`,
          { duration: 6000 }
        );
      }
    }

    // Clear the input value
    e.target.value = null;
  };

  const checkFileValidity = (currentFiles) => {
    // Check all files currently in state to see if any are oversized.
    // This is necessary after a file removal.
    return currentFiles.some(file => {
      if (file.type.startsWith("image/") || file.type.startsWith("image/gif")) {
        return file.size > MAX_IMAGE_SIZE_BYTES;
      } else if (file.type.startsWith("video/")) {
        return file.size > MAX_VIDEO_SIZE_BYTES;
      }
      return false; // Other file types are considered valid size-wise
    });
  };


  const handleRemoveAttachment = (idx) => {
    // Correctly find the file to revoke its URL
    const fileToRemove = files[idx];
    if (fileToRemove) {
      URL.revokeObjectURL(URL.createObjectURL(fileToRemove));
    }
    
    // Filter out the file
    const newFiles = files.filter((_, i) => i !== idx);
    setFiles(newFiles);

    // NEW: Re-check if any remaining files are invalid
    if (newFiles.length > 0) {
      setHasInvalidFile(checkFileValidity(newFiles));
    } else {
      setHasInvalidFile(false); // If no files, there are no invalid files
    }
  };

  const handleOpenFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    // PRE-SUBMISSION GUARDS
    if (!posts_contents.trim() || !posts_type) {
      toast.error("Please fill all required fields");
      return;
    }
    
    // NEW GUARD: Check if there's any invalid file
    if (hasInvalidFile) {
        toast.error("Please remove the oversized attachment(s) before posting.");
        return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("posts_contents", posts_contents);
      formData.append("posts_type", posts_type);

      files.forEach((file) => {
        formData.append("posts_attachments", file);
      });

      const response = await addpostbasedonhrms(formData);

      if (response.status !== "success") {
        toast.error("Failed to publish post!");
      } else {
        toast.success(response.message);
        await getAllPostsWithPagination();
        
        // Resetting state after successful post
        set_posts_contents("");
        set_posts_type("");
        files.forEach((file) => URL.revokeObjectURL(URL.createObjectURL(file)));
        setFiles([]);
        setHasInvalidFile(false); // Reset
        
        handleClose(); 
      }
    } catch (err) {
      console.error("Post failed:", err);
      toast.error("Failed to publish post!");
    } finally {
      setIsLoading(false); // Stop loading regardless of success/failure
    }
  };

  if (!show) return null;

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    const first = parts[0]?.[0] || "";
    const second = parts[1]?.[0] || "";
    return (first + second).toUpperCase();
  };

  // Define the main condition for disabling the Post button
  const isPostDisabled = isLoading || !posts_contents.trim() || !posts_type || hasInvalidFile;

  return (
    <div className="custom-modal-overlay">
      <div className="custom-modal-container" ref={modalRef}>
        {/* Header */}
        <div className="custom-modal-header">
          <p className="head">Publish Post</p>
          <button
            className="custom-modal-close"
            onClick={handleClose}
            disabled={isLoading} 
          >
            <BsX size={28} />
          </button>
        </div>

        {/* Body */}
        <div className="custom-modal-body">
          {/* Loading overlay */}
          {isLoading && (
            <div className="modal-loading-overlay">
              <Loading />
            </div>
          )}

          {/* Textarea */}
          <div className="input-with-profile">
            <div className="avatar mt-2">
              {getInitials(employeeDetails.name)}
            </div>
            <textarea
              className="custom-modal-textarea"
              placeholder="What's on your mind?"
              value={posts_contents}
              onChange={(e) => set_posts_contents(e.target.value)}
              disabled={isLoading} 
            />
          </div>

          {/* Dropdown and Actions */}
          <div className="dropdown-row">
            <select
              className="dropdown-select"
              value={posts_type}
              onChange={(e) => set_posts_type(e.target.value)}
              required
              disabled={isLoading} 
            >
              <option value="">Select Post type</option>
              <option value="new_wins">New Win</option>
              <option value="events">Events</option>
              <option value="news">News</option>
              <option value="appreciations">Appreciation</option>
              <option value="announcements">Announcement</option>
              <option value="others">Others</option>
            </select>

            <div className="d-flex gap-3">
              <button
                className="upload-button-global"
                onClick={handleOpenFileInput}
                type="button"
                disabled={isLoading} 
              >
                <IoDocumentAttachOutline color="#27ae60" />
                Attachment
              </button>

              <button
                className="publish-btn-post"
                onClick={handleSubmit}
                // UPDATED DISABLED CONDITION: Check hasInvalidFile
                disabled={isPostDisabled} 
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Posting...
                  </>
                ) : (
                  "Post"
                )}
              </button>
            </div>
          </div>
          
         


          {/* Previews */}
          <div className="preview-container">
            {files.map((file, idx) => (
              <div className="preview-wrapper" key={idx}>
                {file.type.startsWith("video") ? (
                  <video
                    src={URL.createObjectURL(file)}
                    className="preview-image"
                    controls
                    width="140"
                  />
                ) : (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="preview-image"
                  />
                )}
                <button
                  className="remove-preview"
                  onClick={() => handleRemoveAttachment(idx)}
                  type="button"
                  disabled={isLoading} 
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          accept="image/*,video/*,image/gif"
          multiple
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
          disabled={isLoading} 
        />
      </div>
    </div>
  );
};

export default ModalComponent;